import { useState } from 'react';
import { Inbox } from 'lucide-react';
import { Button, Spinner, useToast } from '@centraoneplus/ui';
import { ThemedSelect } from '@components/common/ThemedSelect';
import { ticketService } from '@services/ticket.service';
import { useAuthStore } from '@stores/authStore';
import { useTeams } from '../../hooks/useTeams';

/** Roles that can ever take a ticket out of a parent team's queue. */
const TRIAGE_ROLES = ['supervisor', 'admin', 'owner'];

function apiErrorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { message?: string } }; message?: string };
  return e?.response?.data?.message || e?.message || fallback;
}

interface ParentQueueTriageProps {
  ticketId: string;
  /** The ticket's current team. */
  teamId?: string | null;
  /** Called after the ticket was moved, so the page can reload it. */
  onMoved: () => void;
}

/**
 * Ticket detail → "Move to sub-team" for a ticket waiting in a PARENT team's
 * queue. A parent team holds no agents, so a ticket routed to it stays
 * unassigned until its head (or a sub-team supervisor) moves it into a
 * sub-team; the pipeline's strategy then picks the agent there.
 *
 * Renders nothing unless the ticket's team is a parent team and the viewer is
 * a supervisor or admin. The server decides who may actually move it — a
 * supervisor who is neither the head nor moving it into their own sub-team is
 * told so.
 */
export function ParentQueueTriage({ ticketId, teamId, onMoved }: ParentQueueTriageProps) {
  const toast = useToast();
  const role = useAuthStore((s) => s.user?.role);
  const canTriage = !!role && TRIAGE_ROLES.includes(role);
  const { data } = useTeams({ isActive: true, limit: 200 }, { enabled: canTriage && !!teamId });
  const [subTeamId, setSubTeamId] = useState('');
  const [isMoving, setIsMoving] = useState(false);

  if (!canTriage || !teamId) return null;
  const team = data?.data.find((t) => t.id === teamId);
  const subTeams = (team?.subTeams ?? []).filter((st) => st.isActive);
  if (!team || subTeams.length === 0) return null;

  const handleMove = async () => {
    const target = subTeams.find((st) => st.id === subTeamId);
    if (!target) return;
    setIsMoving(true);
    try {
      const moved = await ticketService.moveTicketToTeam(ticketId, target.id);
      toast.success(
        `Moved to ${target.name}`,
        moved.assigneeId
          ? 'An agent in that sub-team has been assigned.'
          : 'No agent was available, so it is waiting unassigned in that sub-team.',
      );
      setSubTeamId('');
      onMoved();
    } catch (err) {
      toast.error('Could not move the ticket', apiErrorMessage(err, 'Please try again.'));
    } finally {
      setIsMoving(false);
    }
  };

  return (
    <div
      className="flex flex-wrap items-center gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-sm dark:border-amber-900/50 dark:bg-amber-900/20"
      data-testid="parent-queue-triage"
    >
      <Inbox className="h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
      <span className="text-amber-900 dark:text-amber-200">
        Waiting in the <strong>{team.name}</strong> queue. Move it to a sub-team to get it assigned.
      </span>
      <div className="ml-auto flex items-center gap-2">
        <ThemedSelect
          value={subTeamId}
          onChange={(e) => setSubTeamId(e.target.value)}
          disabled={isMoving}
          aria-label="Sub-team"
          className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        >
          <option value="">Select a sub-team</option>
          {subTeams.map((st) => (
            <option key={st.id} value={st.id}>
              {st.name} ({st.agentCount} {st.agentCount === 1 ? 'agent' : 'agents'})
            </option>
          ))}
        </ThemedSelect>
        <Button size="sm" onClick={handleMove} disabled={!subTeamId || isMoving}>
          {isMoving ? <Spinner size="sm" /> : 'Move to sub-team'}
        </Button>
      </div>
    </div>
  );
}
